import type React from "react";
import type { ReactElement } from "react";

/**
 * Wraps the children JSX elements conditionally based on the provided condition.
 *
 * @param condition - The condition to determine whether to wrap the children or not.
 * @param children - The JSX elements to be wrapped.
 * @param wrapIfTrue - The wrapper component or function to be used if the condition is true.
 * @param wrapIfFalse - The wrapper component or function to be used if the condition is false. Optional.
 * @returns The wrapped JSX elements based on the condition.
 */
export const ConditionalWrap: React.FC<{
  condition: boolean;
  children: React.ReactNode;
  wrapIfTrue: (children: React.ReactNode) => ReactElement;
  wrapIfFalse?: ((children: React.ReactNode) => ReactElement) | null;
}> = ({ condition, children, wrapIfTrue, wrapIfFalse = null }) =>
  condition
    ? wrapIfTrue(children)
    : wrapIfFalse !== null
      ? wrapIfFalse(children)
      : children;
